import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MessageComposerTypeOwnProps } from './interfaces';
import { SendIcon } from '@/shared/icons';
import { Textarea } from '@/shared/Textarea';
import useSheets from '@/hooks/useSheets';
import { useSheetSelection } from '@/contexts/sheetSelection';
import { getNormalizedText, hasSubmittableText } from '@/shared/textSubmit';

const MAX_ATTACHMENTS = 10;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'txt', 'md', 'docx']);

function getFileExtension(fileName: string): string {
  const extension = fileName.split('.').pop();
  return extension ? extension.toLowerCase() : '';
}

function isAllowedFile(file: File): boolean {
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(getFileExtension(file.name));
}

function validateIncomingFiles(incoming: File[], currentCount: number): {
  accepted: File[];
  error: string | null;
} {
  const accepted: File[] = [];
  let rejectedByType = false;
  let rejectedByLimit = false;

  for (const file of incoming) {
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.has(getFileExtension(file.name))) {
      rejectedByType = true;
      continue;
    }

    if (currentCount + accepted.length >= MAX_ATTACHMENTS) {
      rejectedByLimit = true;
      continue;
    }

    accepted.push(file);
  }

  const errorParts: string[] = [];
  if (rejectedByType) {
    errorParts.push('Allowed file types: PDF, TXT, MD, DOCX.');
  }
  if (rejectedByLimit) {
    errorParts.push('Maximum 10 files allowed.');
  }

  return {
    accepted,
    error: errorParts.length > 0 ? errorParts.join(' ') : null,
  };
}

export const TextMessageComposer: FC<MessageComposerTypeOwnProps<string>> = ({
  autoFocus = false,
  submitting,
  onSubmit,
}) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevSubmitting = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    // RAF delay so the click that opened the dropdown doesn't immediately close it
    const rafId = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    });
    function handleOutsideClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownOpen]);
  const { accountId } = useParams<{ accountId?: string }>();
  const { selectedSheetId, setSelectedSheetId, selectedCategoryIds } = useSheetSelection();
  const { sheets, loading: sheetsLoading } = useSheets(accountId);

  const isCategoryMode = selectedCategoryIds.length > 0;

  // Auto-select first sheet if none is selected (only in model mode)
  useEffect(() => {
    if (isCategoryMode) return;
    if (sheetsLoading || sheets.length === 0) return;

    if (selectedSheetId == null) {
      setSelectedSheetId(sheets[0].id);
      return;
    }

    const selectedExists = sheets.some(s => s.id === selectedSheetId);
    if (!selectedExists) {
      setSelectedSheetId(sheets[0].id);
    }
  }, [sheets, sheetsLoading, selectedSheetId, setSelectedSheetId, isCategoryMode]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  // Clear uploading files when submission finishes
  useEffect(() => {
    if (prevSubmitting.current && !submitting) {
      setUploadingFiles([]);
    }
    prevSubmitting.current = submitting;
  }, [submitting]);

  useEffect(() => {
    setFiles(prev => {
      const sanitized = prev.filter(isAllowedFile).slice(0, MAX_ATTACHMENTS);
      return sanitized.length === prev.length ? prev : sanitized;
    });
  }, []);

  const handleSubmit = () => {
    if ((!hasSubmittableText(text) && files.length === 0) || submitting) return;
    const pendingFiles = files.length > 0 ? files : undefined;
    if (pendingFiles) {
      setUploadingFiles(pendingFiles);
      setFiles([]);
      setAttachmentError(null);
    }
    void onSubmit(getNormalizedText(text), pendingFiles);
    setText('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    const snapshot = Array.from(selected);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFiles(prev => {
      const { accepted, error } = validateIncomingFiles(snapshot, prev.length);
      setAttachmentError(error);
      return accepted.length > 0 ? [...prev, ...accepted] : prev;
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const addFiles = useCallback((incoming: File[]) => {
    if (incoming.length === 0) return;

    setFiles(prev => {
      const { accepted, error } = validateIncomingFiles(incoming, prev.length);
      setAttachmentError(error);
      return accepted.length > 0 ? [...prev, ...accepted] : prev;
    });
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pastedFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) pastedFiles.push(file);
      }
    }
    if (pastedFiles.length > 0) {
      e.preventDefault();
      addFiles(pastedFiles);
    }
  }, [addFiles]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, [addFiles]);

  const canSubmit = hasSubmittableText(text) || files.length > 0;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.txt,.md,.docx"
        className="sr-only"
        tabIndex={-1}
        onChange={handleFileChange}
      />

      <div
        className="flex flex-1 flex-col relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[20px] border-2 border-dashed border-pif-green bg-pif-green/5">
            <span className="text-pif-green font-medium text-md">Drop files here</span>
          </div>
        )}
        {(files.length > 0 || uploadingFiles.length > 0) && (
          <div className="flex flex-wrap gap-2 px-6 pt-3">
            {uploadingFiles.map((file, index) => (
              <div
                key={`uploading-${file.name}-${index}`}
                className="flex items-center gap-2 bg-white border border-peach-1 rounded-lg px-3 py-2 text-sm text-grey-hover shadow-sm animate-pulse"
              >
                <svg
                  className="animate-spin shrink-0 text-pif-green"
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="max-w-[200px] truncate">{file.name}</span>
              </div>
            ))}
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center gap-2 bg-white border border-peach-1 rounded-lg px-3 py-2 text-sm text-grey-black shadow-sm"
              >
                <svg className="shrink-0 text-pif-green" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="max-w-[200px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="ml-1 text-grey-hover hover:text-red-500 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        {attachmentError && (
          <div className="px-6 pt-2 text-sm text-red-600">{attachmentError}</div>
        )}
        <div className="flex-1 flex items-start pl-6 pr-2 py-4">
          <Textarea
            autoFocus={autoFocus}
            className="flex-1 text-md resize-none bg-transparent text-grey-black dark:text-grey-plain placeholder-grey-hover dark:placeholder-grey-hover-dark outline-none border-none leading-tight"
            placeholder="Ask something"
            value={text}
            rows={1}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        </div>
      </div>

      {/* + button with dropdown */}
      <div className="relative shrink-0" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(v => !v)}
          className={`flex items-center justify-center w-8 h-8 rounded-full border-[1.5px] transition-all duration-150
            ${dropdownOpen
              ? 'border-pif-green text-pif-green bg-pif-green/5 rotate-45'
              : 'border-grey-medium/60 text-grey-hover hover:border-grey-black hover:text-grey-black'
            }`}
          title="More actions"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>

        {dropdownOpen && (
          <div className="absolute bottom-full right-0 mb-2 w-[210px] bg-white border border-peach-1 rounded-xl shadow-compose py-1.5 z-20">
            <button
              type="button"
              onClick={() => {
                fileInputRef.current?.click();
                setDropdownOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-[14px] text-grey-black hover:bg-peach/40 transition-colors text-left"
            >
              <svg className="shrink-0 text-grey-hover" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              Add files
            </button>
          </div>
        )}
      </div>

      {canSubmit && (
        <button
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className={`
            flex items-center justify-center w-8 h-8 rounded-2xl shrink-0 ml-2
            bg-pif-green text-white transition-colors
            hover:bg-pif-green-light
            disabled:bg-grey-medium disabled:text-white disabled:cursor-not-allowed
          `}
        >
          <SendIcon />
        </button>
      )}
    </>
  );
};
